/**
 * Assignments Table Component
 *
 * Displays learning assignments in a table with status badges,
 * progress bars, and action buttons.
 */

import { useState } from 'react';
import { Trash2, AlertTriangle, Loader2, Video, FileText } from 'lucide-react';
import type { LearningAssignment } from '../../../hooks/useLearning';

interface AssignmentsTableProps {
  assignments: LearningAssignment[];
  isLoading: boolean;
  onDelete: (id: string) => void;
  isDeleting: boolean;
}

export function AssignmentsTable({
  assignments,
  isLoading,
  onDelete,
  isDeleting,
}: AssignmentsTableProps) {
  const [deleteTarget, setDeleteTarget] = useState<string | null>(null);

  const formatDate = (dateString: string | null) => {
    if (!dateString) return '—';
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const getStatusBadge = (status: string, isOverdue: boolean) => {
    if (isOverdue && status !== 'completed') {
      return (
        <span className="inline-flex items-center gap-1 rounded-full bg-red-100 px-2.5 py-1 text-xs font-semibold text-red-700">
          <AlertTriangle size={12} />
          Overdue
        </span>
      );
    }

    switch (status) {
      case 'completed':
        return (
          <span className="rounded-full bg-emerald-100 px-2.5 py-1 text-xs font-semibold text-emerald-700">
            Completed
          </span>
        );
      case 'in_progress':
        return (
          <span className="rounded-full bg-blue-100 px-2.5 py-1 text-xs font-semibold text-blue-700">
            In Progress
          </span>
        );
      default:
        return (
          <span className="rounded-full bg-gray-100 px-2.5 py-1 text-xs font-semibold text-gray-600">
            Assigned
          </span>
        );
    }
  };

  const handleDeleteClick = (id: string) => {
    setDeleteTarget(id);
  };

  const handleConfirmDelete = () => {
    if (deleteTarget) {
      onDelete(deleteTarget);
      setDeleteTarget(null);
    }
  };

  if (isLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-guild-green" />
      </div>
    );
  }

  if (assignments.length === 0) {
    return (
      <div className="flex h-64 flex-col items-center justify-center text-gray-500">
        <div className="mb-3 flex h-16 w-16 items-center justify-center rounded-full bg-gray-100">
          <FileText size={28} className="text-gray-400" />
        </div>
        <p className="text-lg font-medium">No assignments yet</p>
        <p className="text-sm">Assign materials to operators from the Materials tab</p>
      </div>
    );
  }

  return (
    <>
      <div className="overflow-hidden rounded-xl border border-gray-100 bg-white shadow-sm">
        <table className="min-w-full divide-y divide-gray-100">
          <thead className="bg-gradient-to-b from-gray-50 to-gray-50/50">
            <tr>
              <th className="px-5 py-4 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">
                Operator
              </th>
              <th className="px-5 py-4 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">
                Material
              </th>
              <th className="px-5 py-4 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">
                Due Date
              </th>
              <th className="px-5 py-4 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">
                Status
              </th>
              <th className="px-5 py-4 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">
                Progress
              </th>
              <th className="px-5 py-4 text-right text-xs font-semibold uppercase tracking-wider text-gray-500">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-50">
            {assignments.map((assignment) => (
              <tr
                key={assignment.id}
                className="transition-colors hover:bg-gray-50/50"
              >
                {/* Operator */}
                <td className="whitespace-nowrap px-5 py-4">
                  <div className="flex items-center gap-3">
                    <div className="flex h-9 w-9 items-center justify-center rounded-full bg-gradient-to-br from-guild-green to-guild-green/80 text-sm font-bold text-white">
                      {assignment.operator.firstName[0]}
                      {assignment.operator.lastName[0]}
                    </div>
                    <div>
                      <div className="font-medium text-gray-900">
                        {assignment.operator.firstName} {assignment.operator.lastName}
                      </div>
                      <div className="text-xs text-gray-500">
                        {assignment.operator.employeeId}
                      </div>
                    </div>
                  </div>
                </td>

                {/* Material */}
                <td className="px-5 py-4">
                  <div className="flex items-center gap-2">
                    {assignment.material.materialType === 'video' ? (
                      <Video size={16} className="text-violet-500" />
                    ) : (
                      <FileText size={16} className="text-amber-500" />
                    )}
                    <span className="max-w-[200px] truncate font-medium text-gray-900">
                      {assignment.material.title}
                    </span>
                  </div>
                </td>

                {/* Due Date */}
                <td className="whitespace-nowrap px-5 py-4 text-sm text-gray-600">
                  {formatDate(assignment.dueDate)}
                </td>

                {/* Status */}
                <td className="whitespace-nowrap px-5 py-4">
                  {getStatusBadge(assignment.status, assignment.isOverdue)}
                </td>

                {/* Progress */}
                <td className="px-5 py-4">
                  <div className="flex items-center gap-3">
                    <div className="h-2 w-24 overflow-hidden rounded-full bg-gray-100">
                      <div
                        className={`h-full rounded-full transition-all duration-500 ${
                          assignment.progress === 100
                            ? 'bg-emerald-500'
                            : assignment.progress > 0
                            ? 'bg-blue-500'
                            : 'bg-gray-200'
                        }`}
                        style={{ width: `${assignment.progress}%` }}
                      />
                    </div>
                    <span className="text-sm font-medium text-gray-600">
                      {assignment.progress}%
                    </span>
                  </div>
                </td>

                {/* Actions */}
                <td className="whitespace-nowrap px-5 py-4 text-right">
                  <button
                    onClick={() => handleDeleteClick(assignment.id)}
                    className="rounded-lg p-2 text-gray-400 transition-colors hover:bg-red-50 hover:text-red-600"
                    title="Delete assignment"
                  >
                    <Trash2 size={16} />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Delete Confirmation Modal */}
      {deleteTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-xl">
            <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-red-100">
              <AlertTriangle className="h-6 w-6 text-red-600" />
            </div>
            <h3 className="mb-2 text-lg font-semibold text-gray-900">
              Delete Assignment?
            </h3>
            <p className="mb-6 text-sm text-gray-500">
              This will remove the assignment from the operator. This action cannot be undone.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setDeleteTarget(null)}
                className="flex-1 rounded-xl border border-gray-200 px-4 py-2.5 text-sm font-semibold text-gray-700 transition-colors hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmDelete}
                disabled={isDeleting}
                className="flex flex-1 items-center justify-center gap-2 rounded-xl bg-red-600 px-4 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-red-700 disabled:opacity-50"
              >
                {isDeleting ? (
                  <>
                    <Loader2 size={16} className="animate-spin" />
                    Deleting...
                  </>
                ) : (
                  'Delete'
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
